/**
 * Surface-item planners — small decor placed ON TOP of flat-topped furniture
 * (desks, dressers, bookshelves, minifridges). Today: surface plants. This is
 * also where surface knickknacks join, since "find a host, check its top,
 * seat an item on it" is the same problem.
 */

const {
  isPointInsidePolygon,
} = require('../../bathroom-analyzer/pipeline/geometry');
const { footprintFor } = require('../../geometry/objectFootprint');
const {
  freeZonesFromOccupied,
} = require('../../dorm-optimizer-v2/placement/geometry-gaps');
const {
  DESK_PLANT_DIMS,
  SURFACE_PLANT_HOST_TYPES,
  KNICKKNACK_DIMS,
  KNICKKNACK_GAP_M,
  KNICKKNACK_EDGE_MARGIN_M,
} = require('./constants');
const {
  nearestWindowDist,
  objectWorldFromLocal,
  objectLocalX,
} = require('./shared');
const { packRowAcross, dealAcrossSlots } = require('./packRow');

/**
 * True when a furniture object sits ON TOP of `host` (occupying its top surface)
 * — i.e. its XZ center lies inside the host's footprint AND its bottom
 * (`center.y − dims.y/2`) is at or above the host's top. Used to skip placing a
 * surface plant on a desk that already carries a hutch/monitor.
 */
function isTopOccupied(host, hostCorners, hostTop, objects) {
  for (const obj of objects) {
    if (obj === host) continue;
    const oc = obj?.center;
    const od = obj?.dimensions;
    if (!oc || !od) continue;
    // XZ center inside the host footprint?
    const inside = isPointInsidePolygon({ x: oc.x, z: oc.z }, hostCorners);
    if (!inside) continue;
    // Bottom of the candidate at/above the host top → it's sitting on top.
    const objBottom = oc.y - (od.y || 0) / 2;
    if (objBottom >= hostTop - 1e-3) return true;
  }
  return false;
}

/**
 * Place small `dorm-desk-plant` succulents on flat-topped furniture tops. Walks
 * the whitelist of host types, skips any host whose top is already occupied
 * (a hutch/monitor sitting on it), and emits at the top-center of each until
 * `count` plants are placed. Each carries `attributes.potStyle` so the renderer
 * can pick the pot. Wall/floor collision-immune by construction (they sit ON
 * existing furniture that already fits).
 *
 * @returns {Array<Object>} desk-plant entries (≤ count)
 */
function planSurfacePlants(room, count, potStyle, idPrefix, windowCenters) {
  const n = Math.max(0, Math.floor(count || 0));
  if (n === 0) return [];

  const objects = room?.objects || [];
  const plantHalfH = DESK_PLANT_DIMS.y / 2;
  const entries = [];

  // Flat-topped host furniture, ranked by proximity to a window (sunniest first)
  // so a shelf/desk plant lands where it gets light, not in a dark corner.
  const hosts = objects
    .filter((h) => {
      const type = (h?.type || '').toLowerCase();
      return SURFACE_PLANT_HOST_TYPES.has(type) && h?.center && h?.dimensions;
    })
    .sort(
      (a, b) =>
        nearestWindowDist(a.center, windowCenters) -
        nearestWindowDist(b.center, windowCenters)
    );

  for (const host of hosts) {
    if (entries.length >= n) break;
    const hc = host.center;
    const hd = host.dimensions;

    const hostTop = hc.y + (hd.y || 0) / 2;
    const hostCorners = footprintFor(
      hc,
      hd.x || 0,
      hd.z || 0,
      host?.rotation?.y || 0
    );
    if (isTopOccupied(host, hostCorners, hostTop, objects)) continue;

    entries.push({
      id: `${idPrefix}-surface-plant-${host.id || entries.length}`,
      type: 'dorm-desk-plant',
      category: { 'dorm-desk-plant': {} },
      center: { x: hc.x, y: hostTop + plantHalfH, z: hc.z },
      rotation: { x: 0, y: 0, z: 0 },
      dimensions: { ...DESK_PLANT_DIMS },
      story: 0,
      attributes: { potStyle },
      dormProvided: false,
      parentWallIds: [],
    });
  }

  return entries;
}

/* ── knickknacks ────────────────────────────────────────────────────── */

/**
 * Occupied intervals along the host top's local X, in [0, hostWidth] space —
 * every object (persisted furniture AND decor emitted earlier this pass, e.g.
 * surface plants) whose XZ center sits inside the host footprint at/above its
 * top projects to `center ± width/2`. Feeds freeZonesFromOccupied, the same
 * 1-D primitive the wall planners block spans with.
 */
function topOccupiedIntervals(host, hostCorners, hostTop, objects) {
  const halfW = (host.dimensions?.x || 0) / 2;
  const intervals = [];
  for (const obj of objects) {
    if (obj === host) continue;
    const oc = obj?.center;
    const od = obj?.dimensions;
    if (!oc || !od) continue;
    if (!isPointInsidePolygon({ x: oc.x, z: oc.z }, hostCorners)) continue;
    if (oc.y - (od.y || 0) / 2 < hostTop - 1e-3) continue;
    const lx = objectLocalX(host, oc) + halfW; // → [0, hostWidth]
    const itemHalf = Math.max(od.x || 0, od.z || 0) / 2;
    intervals.push({
      start: Math.max(0, lx - itemHalf),
      end: Math.min(2 * halfW, lx + itemHalf),
    });
  }
  return intervals.filter((iv) => iv.end > iv.start);
}

// Knickknacks want to live where their owner works: desks first, then
// dressers, then the rest of the surface-host whitelist.
const KNICKKNACK_HOST_ORDER = ['dorm-desk', 'dorm-dresser'];

function knickknackHostRank(type) {
  const i = KNICKKNACK_HOST_ORDER.indexOf(type);
  return i === -1 ? KNICKKNACK_HOST_ORDER.length : i;
}

/**
 * Place small personal knickknacks (rubik's cube, plush, book stack, mug,
 * snacks, candle) on flat-topped furniture, packed side-by-side in each
 * host's largest FREE top interval — free means clear of hutches/monitors in
 * `room.objects` AND of decor emitted earlier this pass (`priorEntries`, the
 * surface plants), so a plant and a mug never share a spot. Items that don't
 * fit anywhere are DROPPED, never squeezed.
 *
 * Depth seating is a half-back bias (local −z, the `objectFootprint` "toward
 * the wall" side): pushed fully against the back edge a mug vanishes behind a
 * hutch, dead-center wastes the natural desk look — and the half bias keeps
 * everything on the surface even for a host that breaks the facing
 * convention.
 *
 * @param {Object} room
 * @param {Array<{type:string,count:number,color?:number}>} spec  bundle.knickknacks;
 *   `color` is a palette index for the piece's tint
 * @param {string[]} palette  bundle.palette (hex strings)
 * @param {string} idPrefix
 * @param {Array<Object>} priorEntries  decor already emitted this pass
 * @returns {Array<Object>} knickknack entries (≤ Σcount)
 */
function planSurfaceKnickknacks(room, spec, palette, idPrefix, priorEntries) {
  if (!Array.isArray(spec) || spec.length === 0) return [];

  // Expand the spec into an ordered wish list of concrete items.
  const wanted = [];
  for (const entry of spec) {
    const type = entry?.type;
    if (!type || !KNICKKNACK_DIMS[type]) continue; // registered types only
    const count = Math.max(0, Math.floor(entry?.count || 0));
    for (let i = 0; i < count; i++) {
      wanted.push({ type, colorIndex: entry.color });
    }
  }
  if (wanted.length === 0) return [];

  const objects = room?.objects || [];
  const scanObjects = [...objects, ...(priorEntries || [])];
  const hosts = objects
    .filter((h) => {
      const type = (h?.type || '').toLowerCase();
      return SURFACE_PLANT_HOST_TYPES.has(type) && h?.center && h?.dimensions;
    })
    .sort(
      (a, b) =>
        knickknackHostRank((a.type || '').toLowerCase()) -
        knickknackHostRank((b.type || '').toLowerCase())
    );

  // Measure every host FIRST, then deal items out round-robin.
  //
  // Filling each host greedily before moving on put four of cabincore's five
  // knickknacks on one dresser and left the rest of the room bare — correct
  // by the packing rule, wrong for the room. Dealing one piece per host per
  // pass spreads them, and only doubles up once every host has something.
  const slots = [];
  for (const host of hosts) {
    const hc = host.center;
    const hd = host.dimensions;
    const hostW = hd.x || 0;
    if (hostW <= 0) continue;

    const hostTop = hc.y + (hd.y || 0) / 2;
    const hostCorners = footprintFor(
      hc,
      hd.x || 0,
      hd.z || 0,
      host?.rotation?.y || 0
    );
    // See planRug: null means the host has no placeable centre.
    if (!hostCorners) continue;
    const occupied = topOccupiedIntervals(
      host,
      hostCorners,
      hostTop,
      scanObjects
    );
    const zones = freeZonesFromOccupied(occupied, hostW);
    let zone = null;
    for (const z of zones) {
      if (!zone || z.end - z.start > zone.end - zone.start) zone = z;
    }
    if (!zone) continue;
    const usable = zone.end - zone.start - 2 * KNICKKNACK_EDGE_MARGIN_M;
    if (usable <= 0) continue;

    slots.push({
      host,
      hd,
      hostTop,
      usable,
      zoneCenterLocal: (zone.start + zone.end) / 2 - hostW / 2,
      chosen: [],
      used: 0,
    });
  }

  dealAcrossSlots(slots, wanted, {
    widthOf: (item) => KNICKKNACK_DIMS[item.type].x,
    gap: KNICKKNACK_GAP_M,
  });

  const entries = [];
  let placedTotal = 0; // stable index for colour rotation + variant seed

  for (const slot of slots) {
    if (slot.chosen.length === 0) continue;
    const { host, hd, hostTop, usable, zoneCenterLocal, chosen } = slot;
    const { offsets, placedCount } = packRowAcross(
      usable,
      chosen.map((wItem) => KNICKKNACK_DIMS[wItem.type].x),
      { gap: KNICKKNACK_GAP_M }
    );
    // The dealer and the packer now share `rowFits`/`extendRow`, so these agree
    // by construction rather than by comment. Clamp anyway: an offsets[i] of
    // undefined would place a piece at NaN, which renders as nothing at all
    // rather than as an error.
    const emit = Math.min(chosen.length, placedCount);

    for (let i = 0; i < emit; i++) {
      const item = chosen[i];
      const dims = KNICKKNACK_DIMS[item.type];
      const lx = zoneCenterLocal + offsets[i];
      // Half-back depth bias (see docstring), clamped to keep the footprint on.
      const lz = -Math.max(0, ((hd.z || 0) - dims.z) / 4);
      const pos = objectWorldFromLocal(host, lx, lz);
      const paletteLen = (palette || []).length;
      const slotIndex = placedTotal + i;
      const color =
        paletteLen > 0
          ? palette[
              (item.colorIndex != null ? item.colorIndex : slotIndex) %
                paletteLen
            ]
          : undefined;
      entries.push({
        id: `${idPrefix}-knickknack-${item.type}-${host.id || 'host'}-${i}`,
        type: item.type,
        category: { [item.type]: {} },
        center: { x: pos.x, y: hostTop + dims.y / 2, z: pos.z },
        rotation: { x: 0, y: host?.rotation?.y || 0, z: 0 },
        dimensions: { ...dims },
        story: 0,
        attributes: {
          ...(color ? { themeColor: color } : {}),
          // Deterministic per-slot variation seed — stamped (not id-derived)
          // so a piece looks identical in preview and after save re-ids it.
          variant: ((slotIndex + 1) * 7919) | 0,
        },
        dormProvided: false,
        parentWallIds: [],
      });
    }
    placedTotal += emit;
  }

  return entries;
}

module.exports = {
  isTopOccupied,
  // Exported for the staging planner, which packs the same host tops with a
  // different policy. Copying it would be a second implementation of "what is
  // already standing on this surface".
  topOccupiedIntervals,
  planSurfacePlants,
  planSurfaceKnickknacks,
};
